import React from 'react';
import { motion } from 'framer-motion';
import { WarningIcon, XMarkIcon } from '../icons/SalesIcons';
import { SectionDivider } from '../ui/SectionDivider';
export function PainSection() {
  const failedSolutions = [{
    tried: 'Tutoriales de YouTube',
    problem: 'Información fragmentada, sin estructura, sin soporte'
  }, {
    tried: 'Grupos de Facebook',
    problem: 'Consejos contradictorios, sin orden pedagógico'
  }, {
    tried: 'Ensayo y error',
    problem: 'Materiales desperdiciados, frustración acumulada'
  }, {
    tried: 'Talleres presenciales',
    problem: 'Caros, una sola sesión, no puedes repasar'
  }, {
    tried: 'Cursos genéricos',
    problem: 'Sin profundidad técnica, sin comunidad, sin seguimiento'
  }];
  return <section className="bg-[#FFFAF5] py-16 md:py-24" data-id="element-323">
      <div className="max-w-4xl mx-auto px-4" data-id="element-324">
        {/* Opening Story */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="mb-16" data-id="element-325">
          <p className="text-xl md:text-2xl text-[#5C4033] leading-relaxed mb-6 font-heading italic" data-id="element-326">
            ¿Recuerdas la primera vez que intentaste hacer una vela con un
            tutorial de YouTube?
          </p>

          <div className="space-y-4 text-lg text-[#2D2926] leading-relaxed" data-id="element-327">
            <p data-id="element-328">
              Compraste la cera. Conseguiste el tarro. Seguiste cada paso al pie
              de la letra.
            </p>

            <p data-id="element-329">
              Y cuando la encendiste... olía raro. Tenía huecos en el centro. La
              mecha se ahogó a los cinco minutos.
            </p>

            <p data-id="element-330">
              Gastaste dinero. Gastaste tiempo. Y lo peor: empezaste a pensar
              que quizás esto "no era para ti."
            </p>
          </div>

          <div className="mt-8 p-6 bg-white rounded-xl border-l-4 border-[#9B2335] shadow-lg" data-id="element-331">
            <p className="text-xl font-bold text-[#9B2335] mb-4" data-id="element-332">
              Pero aquí está la verdad que nadie te dijo:
            </p>
            <p className="text-[#2D2926] leading-relaxed" data-id="element-333">
              No fallaste porque no tienes talento. Fallaste porque nadie te
              enseñó la fórmula correcta. Nadie te explicó cómo elegir el pabilo
              según el diámetro del recipiente. Nadie te habló de los
              porcentajes exactos de fragancia. Nadie te mostró por qué la cera
              que elegiste no era la adecuada para lo que querías hacer.
            </p>
            <p className="text-xl font-bold text-[#9B2335] mt-4" data-id="element-334">
              Y eso — exactamente eso — es lo que cambia hoy.
            </p>
          </div>
        </motion.div>

        <SectionDivider variant="flame" data-id="element-335" />

        {/* Pain Section */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="mb-16" data-id="element-336">
          <div className="flex items-center gap-3 mb-8" data-id="element-337">
            <WarningIcon size={32} className="text-[#9B2335]" data-id="element-338" />
            <h2 className="font-heading text-3xl md:text-4xl text-[#5C4033]" data-id="element-339">
              El Dolor Que Nadie Nombra
            </h2>
          </div>

          <div className="space-y-4 text-lg text-[#2D2926] leading-relaxed mb-8" data-id="element-340">
            <p data-id="element-341">Quizás ya intentaste hacer velas antes.</p>

            <p data-id="element-342">
              Compraste cera, compraste esencias, compraste el tarro más bonito
              que encontraste.
            </p>

            <p data-id="element-343">
              Y la vela te quedó con{' '}
              <strong className="text-[#9B2335]" data-id="element-344">
                un túnel horrible en el centro.
              </strong>{' '}
              O sudaba aceite. O no olía a nada cuando se quemaba. O la mecha se
              apagaba sola a los tres minutos.
            </p>

            <p data-id="element-345">
              Y te preguntaste: <em data-id="element-346">"¿Qué hice mal?"</em>
            </p>

            <p data-id="element-347">
              La respuesta es incómoda:{' '}
              <strong data-id="element-348">hiciste exactamente lo que te enseñaron.</strong> Y lo que
              te enseñaron estaba incompleto.
            </p>

            <p data-id="element-349">
              Los tutoriales gratuitos te muestran el "qué" pero nunca el "por
              qué".
            </p>

            <p data-id="element-350">
              Nadie te habló de que{' '}
              <strong data-id="element-351">
                cada tipo de cera tiene un porcentaje de fragancia diferente.
              </strong>{' '}
              Que el pabilo incorrecto puede arruinar hasta la mejor fórmula.
              Que la temperatura en que agregas el colorante cambia el resultado
              final. Que hay una tabla — una fórmula exacta — que separa las
              velas aficionadas de las que se venden en tiendas de diseño.
            </p>

            <p data-id="element-352">
              Esa información existía. Solo que nadie te la dio organizada, en
              orden, con ejemplos reales y alguien al otro lado que respondiera
              tus dudas.
            </p>

            <p className="text-xl font-bold text-[#9B2335]" data-id="element-353">Hasta ahora.</p>
          </div>
        </motion.div>

        <SectionDivider variant="line" data-id="element-354" />

        {/* Failed Solutions Table */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} data-id="element-355">
          <h3 className="font-heading text-2xl md:text-3xl text-[#5C4033] text-center mb-8" data-id="element-356">
            Por Qué Todo Lo Que Has Intentado Hasta Hoy No Ha Funcionado
          </h3>

          <div className="space-y-4" data-id="element-357">
            {failedSolutions.map((item, index) => <motion.div key={item.tried} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-white rounded-xl p-5 shadow-md border border-[#D4A0A0]/30 flex flex-col md:flex-row md:items-center gap-4" data-id="element-358">
                <div className="flex items-center gap-3 md:w-1/3" data-id="element-359">
                  <XMarkIcon size={24} className="text-[#9B2335] flex-shrink-0" data-id="element-360" />
                  <span className="font-bold text-[#5C4033]" data-id="element-361">{item.tried}</span>
                </div>
                <div className="md:w-2/3 pl-9 md:pl-0" data-id="element-362">
                  <p className="text-[#2D2926]" data-id="element-363">{item.problem}</p>
                </div>
              </motion.div>)}
          </div>

          <p className="text-center text-xl font-bold text-[#9B2335] mt-8" data-id="element-364">
            El problema nunca fue tu dedicación. Fue la falta de un sistema
            probado, completo y progresivo.
          </p>
        </motion.div>
      </div>
    </section>;
}